import { CircleInfo } from "@/icons/CircleInfo.tsx";
import { CircleXMark } from "@/icons/CircleXMark.tsx";
import { PhotoModel } from "@/models/photo.model.ts";
import { Loading } from "@/ui/shared/Loading.tsx";
import { PhotoCard } from "@/ui/shared/PhotoCard.tsx";
import { Alert } from "@mantine/core";
import { motion } from "framer-motion";

type PhotoGridProps = {
  data?: { pages: { data: PhotoModel[] }[] };
  form?: any;
  handlePhotoClick: (pageIndex: number, photoIndex: number) => void;
  showAI?: boolean;
  isFetching: boolean;
  isError: boolean | undefined;
  inViewRef: (node?: Element | null) => void;
  onPhotoCopySettings?: (photo: PhotoModel) => void;
};

export function PhotoGrid(props: PhotoGridProps) {
  const {
    data,
    form,
    handlePhotoClick,
    isFetching,
    showAI = true,
    isError = false,
    inViewRef,
    onPhotoCopySettings,
  } = props;

  if (isError)
    return (
      <Alert color="red" icon={<CircleXMark size={20} />} className="w-full">
        Unable to load photos. Please try again later.
      </Alert>
    );

  if (data?.pages[0].data.length === 0)
    return (
      <Alert
        bd="1px dashed"
        color="grape"
        icon={<CircleInfo size={20} />}
        className="w-full"
      >
        No photos found!
      </Alert>
    );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-4 gap-3">
      {data?.pages.map((page, pageIndex) =>
        page.data.map((photo, photoIndex) => (
          <motion.div
            className="relative flex justify-center"
            key={photo.id}
            layout
            whileHover={photo.url ? { scale: 1.025 } : {}}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <PhotoCard
              onPhotoCopySettings={onPhotoCopySettings}
              photo={photo}
              showAI={showAI}
              onClick={() => handlePhotoClick(pageIndex, photoIndex)}
              form={form}
            />
          </motion.div>
        )),
      )}
      <div
        ref={inViewRef}
        className="col-span-full flex flex-col w-full justify-center"
      >
        {isFetching && <Loading />}
      </div>
    </div>
  );
}
